<template>
    <div class="payment-method-card">
        <h5 v-if="thisCard.is_default == 1" class="default-label">DEFAULT</h5>
        <div style="display: flex; flex-direction: row;">
            <span>
                <VisaIcon v-if="thisCard.cc_type == 'Visa'" alt="visa-logo"/>
                <MasterCardIcon v-else-if="thisCard.cc_type == 'MasterCard' || thisCard.cc_type == 'Master Card'" alt="mastercard-logo"/>
                <AmexIcon v-else-if="thisCard.cc_type == 'American Express'" alt="amex-logo"/>
                <DiscoverIcon v-else-if="card.cc_type == 'Discover'" alt="discover-logo"/>
            </span>
            &nbsp;
            <p class="text-rothcoBlack">{{ thisCard.cc_type }} &bull;&bull;&bull;&bull; {{ thisCard.cc_last_4 }}</p>
        </div>
        <div style="padding: 10px 0;" v-if="isEditing" class="field-state input-folder">
            <div class="input-folder--input">
                <InputMask
                    :aria-required="true"
                    v-model="exp_inputVal" 
                    id="card_exp" 
                    placeholder=" " 
                    required 
                    mask="99/99"
                    aria-label="Card Expiration Date"
                    maxlength="48"
                    :class="{
                        'invalid-input-state': errors.cc_exp || !cardExpValid,
                    }"
                />
                <label for="card_exp">
                    <p class="paragraph-default">Card Expiration Date (MM/YY) *</p>
                </label>
            </div>
            <p class="input-support-text invalid-state" v-if="errors.cc_exp">{{ errors.cc_exp }}</p>
            <p class="input-support-text invalid-state" v-else-if="!cardExpInRange">Expiration Date is invalid</p>
            <p class="input-support-text invalid-state" v-else-if="!cardExpValid">Expiration Date is required</p>
            
        </div>
        <div style="padding: 10px 0;" v-if="isEditing" class="field-state input-folder">
            <div class="input-folder--input">
                <InputText
                    v-model="alias_inputVal" 
                    id="card_alias" 
                    placeholder=" "
                    aria-label="Card Expiration Date"
                    maxlength="48"
                    :class="{
                        'invalid-input-state': errors.cc_alias,
                    }"
                />
                <label for="card_alias">
                    <p class="paragraph-default">Card Alias</p>
                </label>
            </div>
            <p class="input-support-text invalid-state" v-if="errors.cc_alias">{{ errors.cc_alias }}</p>
        </div>
        <p v-if="!isEditing">Expiration: {{ thisCard.cc_exp_month }}/{{ thisCard.cc_exp_year.slice(-2) }}</p>
        <p v-if="!isEditing">Alias: {{ thisCard.cc_alias}}</p>
        <p v-if="genericErrorMessages.length > 0" class="input-support-text invalid-state">
            {{ genericErrorMessages.join(', ') }}
        </p>
        <div style="gap: 1rem; display: flex; flex-direction: row;">
            <button v-if="!isEditing" type="button" class="primary" @click="isEditing = true" id="edit_button">Edit</button>
            <button v-if="isEditing" type="button" class="primary" @click="saveCard" id="save_button">Save</button>
            <button v-if="isEditing" type="button" class="primary" @click="cancelEdit" id="cancel_edit_button">Cancel</button>
            <DeleteIcon id="delete_button" style="cursor: pointer; width: 1.5rem;" @click="openDeletePaymentMethodModal(thisCard.credit_card_id)" />
        </div>
        <a id="set_as_default" @click="setAsDefault" v-if="!thisCard.is_default">Set as Default</a>
    </div>
</template>
<script lang="ts" setup>
    import { ref, computed, defineEmits, inject } from "vue";
    import { useDialog } from "primevue/usedialog";
    import { usePaymentMethod } from "@/helpers/composables/paymentMethod"
    import { router } from "@inertiajs/vue3";
    import { useRouter } from "@/helpers/composables/useRouter";
    import DeletePaymentMethodModal from "./DeletePaymentMethodModal.vue";
    import InputMask from "primevue/inputmask";
    import InputText from "primevue/inputtext";
    import VisaIcon from "@icons/visa.svg";
    import MasterCardIcon from "@icons/mastercard.svg";
    import AmexIcon from "@icons/amex.svg";
    import DiscoverIcon from "@icons/discover.svg";
    import DeleteIcon from "@icons/delete-black.svg";

    let route = inject("route");
    const emit = defineEmits(['deleteCreditCard', 'updateCreditCard', 'openDeleteModal', 'setDefault']);
    let props = defineProps({
        card: {
            type: Object,
            default(){
                return {};
            }
        },
        user: {
            type: Object,
            default(){
                return {};
            }
        }
    });

    const thisCard = defineModel({default: {
        "credit_card_id": null,
        "cc_name": null,
        "cc_type": null,
        "is_default": null,
        "company_id": null,
        "cc_last_4": null,
        "cc_alias": null,
        "cc_exp_month": null,
        "cc_exp_year": null,
        "gateway_id":null
    }})

    let {
        updateConsumerCreditCard,
        updateCompanyCreditCard,
        errors
    } = usePaymentMethod()

    let { to } = useRouter({
        router,
        route
    });

    let isEditing = ref(false);
    let exp_inputVal = ref(thisCard.value.cc_exp_month && thisCard.value.cc_exp_year.slice?.(-2) ? `${thisCard.value.cc_exp_month}/${thisCard.value.cc_exp_year.slice?.(-2)}` : null);
    let alias_inputVal = ref(thisCard.value.cc_alias ? `${thisCard.value.cc_alias}` : null);
    let saveCard = () => {
        if(cardExpValid.value && cardExpInRange.value){
            let currAlias = thisCard.value.cc_alias ? `${thisCard.value.cc_alias}` : null; 
            let currExp = thisCard.value.cc_exp_month && thisCard.value.cc_exp_year.slice?.(-2) ? `${thisCard.value.cc_exp_month}/${thisCard.value.cc_exp_year.slice?.(-2)}` : null;
            if(exp_inputVal.value != currExp){
                currExp = exp_inputVal.value;
            }
            if(alias_inputVal.value != currAlias){
                currAlias = alias_inputVal.value;
            }
            currExp = currExp?.replace("/", "") ?? null;
            if(props.user.is_dealer) {
                let data = {
                    credit_card_id: thisCard.value.credit_card_id,
                    cc_alias: currAlias,
                    cc_exp: currExp,
                }
                updateCompanyCreditCard(data, {
                    afterFetch: (ctx)=>{
                        thisCard.value.cc_alias = data.cc_alias;
                        thisCard.value.cc_exp_month = data.cc_exp?.slice?.(0, 2);
                        thisCard.value.cc_exp_year = `20${data.cc_exp?.slice?.(-2)}`
                        isEditing.value = false;
                    }
                })
            } else {
                let data = {
                    consumer_credit_card_id: thisCard.value.credit_card_id,
                    cc_alias: currAlias,
                    cc_exp: currExp
                }
                updateConsumerCreditCard(data, {
                    afterFetch: (ctx)=>{
                        thisCard.value.cc_alias = data.cc_alias;
                        thisCard.value.cc_exp_month = data.cc_exp?.slice?.(0, 2);
                        thisCard.value.cc_exp_year = `20${data.cc_exp?.slice?.(-2)}`
                        isEditing.value = false;
                    }
                })
            }
        }
    };
    let cancelEdit = () => {
        isEditing.value = false;
        exp_inputVal.value = thisCard.value.cc_exp_month && thisCard.value.cc_exp_year.slice?.(-2) ? `${thisCard.value.cc_exp_month}/${thisCard.value.cc_exp_year.slice?.(-2)}` : null;
        alias_inputVal.value = thisCard.value.cc_alias ? `${thisCard.value.cc_alias}` : null; 
    };

    let cardExpValid = computed(()=>{
        return !exp_inputVal.value?.includes('_') && exp_inputVal.value?.replace(/\D/g, "") != null && exp_inputVal.value?.replace(/\D/g, "") != '';
    });
    let cardExpInRange = computed(()=>{
        if(cardExpValid.value){
            const month: string = exp_inputVal.value?.split("/")[0] ?? '';
            const year: string = exp_inputVal.value?.split("/")[1] ?? '';
            const now = new Date();
            const exp = new Date(`20${year}`, month);
            return exp > now && parseInt(month) > 0 && parseInt(month) < 13;
        }
        return true;
    });
    
    let setAsDefault = () => {
        if(props.user.is_dealer){
            let data = {
                credit_card_id: thisCard.value.credit_card_id,
                is_default: 1
            }
            updateCompanyCreditCard(data, {
                afterFetch: (ctx)=>{
                    emit('setDefault', thisCard.value.credit_card_id);
                }
            })
        } else {
            let data = {
                consumer_credit_card_id: thisCard.value.credit_card_id,
                is_default: 1
            }
            updateConsumerCreditCard(data, {
                afterFetch: (ctx)=>{
                    emit('setDefault', thisCard.value.credit_card_id);
                }
            })
        }
        return;
    };

    const dialog = useDialog();

    const openDeletePaymentMethodModal = (id) => {
        emit('openDeleteModal', id);
        dialog.open(DeletePaymentMethodModal, {
            props: {
                modal: true,
                showHeader: false
            },
            data: {
                credit_card_id: id,
                deletePaymentMethod: (id)=>{
                    emit('deleteCreditCard', id);
                }
            }
        });
    };

    let genericErrorMessages = computed(()=>{
        // check if the error object has keys that aren't of the inputs we are using
        return Object.keys(errors.value).filter((key) => {
            return !['cc_alias', 'cc_exp'].includes(key);
        }).reduce((acc, key) => {
            acc.push(errors.value[key]);
            return acc;
        }, []);
    });
</script>